import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Upload, AlertTriangle } from "lucide-react";
import { Spinner } from "./ui/kit";
import { fmtKc } from "../lib/format";
import { ikonaTechnologie } from "../lib/technologie";
import api, { apiError } from "../lib/api";
import { DruhStitek, StavUhrady, kratkeDatum, NahratFakturu, PrirazeniDialog, TlacitkoNahrat } from "./FakturyPripadu";

const pct = (n) => `${String(n).replace(".", ",")} %`;

/**
 * Záložka Ekonomika obchodního případu: vydané faktury klientovi (nahrané
 * z účetního programu), kolik zbývá vyfakturovat a náklady a výsledek
 * každé technologie. Vše bez DPH, jen „k vyfakturování" ukazuje i daň.
 */
export default function PripadEkonomika({ pripadId, nazev }) {
  const nav = useNavigate();
  const [d, setD] = useState(undefined);
  const [err, setErr] = useState("");
  const [nahrat, setNahrat] = useState(null);   // vybrané soubory
  const [prirazeni, setPrirazeni] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/api/obchodni-pripady/${pripadId}/ekonomika`); setD(data); }
    catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); setD(null); }
  }, [pripadId]);
  useEffect(() => { load(); }, [load]);

  if (d === undefined) return <div className="p-8 flex justify-center"><Spinner /></div>;
  if (d === null) return <div className="p-6 t-telo text-danger">{err || "Ekonomiku se nepodařilo načíst."}</div>;

  const s = d.souhrn;
  const technologie = d.technologie.map((t) => ({ id: t.id, nazev: t.nazev, cena: t.plan?.prodej || 0 }));
  const sPlanem = d.technologie.some((t) => t.plan);

  return (
    <div className="rise" data-testid="pripad-ekonomika">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border-b border-line" data-testid="eko-souhrn">
        <Dlazdice label="Vyfakturováno" hodnota={s.vyfakturovano} />
        <Dlazdice label="Náklady" hodnota={s.naklady} />
        <Dlazdice label="Výsledek" hodnota={s.vysledek} barva={s.vysledek < 0 ? "text-danger" : s.vysledek > 0 ? "text-success" : "text-ink"}
          pod={s.vysledek_pct != null ? pct(s.vysledek_pct) : null} />
        <Dlazdice label="Zbývá vyfakturovat" hodnota={s.zbyva_vyfakturovat} />
      </div>

      {/* Vydané faktury klientovi */}
      <div className="px-4 md:px-5 pt-4 pb-2.5 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="t-sekce font-700 text-ink">Vydané faktury klientovi</h3>
          <p className="t-meta text-muted mt-0.5">Vystavíš je ve svém účetním programu a nahraješ sem — přijaté od dodavatelů taky, po přečtení jen potvrdíš, co je co. Částky bez DPH.</p>
        </div>
        <TlacitkoNahrat onSoubory={setNahrat} testid="eko-nahrat" className="hidden md:inline-flex btn-primary shrink-0">
          <Upload size={15} /> Nahrát fakturu
        </TlacitkoNahrat>
      </div>
      <div className="px-4 pb-3 md:hidden">
        <TlacitkoNahrat onSoubory={setNahrat} testid="eko-nahrat-mobil"
          className="w-full h-12 rounded-pill bg-accent text-white t-telo font-700 inline-flex items-center justify-center gap-2">
          <Upload size={16} /> Nahrát fakturu
        </TlacitkoNahrat>
      </div>
      {s.neprirazenych_vydanych > 0 && (
        <div className="mx-4 md:mx-5 mb-2 flex items-start gap-2 rounded-input bg-accent/[0.07] px-3 py-2.5 t-meta text-ink" data-testid="eko-neprirazene">
          <AlertTriangle size={15} className="text-accent-hover shrink-0 mt-0.5" />
          <span>{s.neprirazenych_vydanych === 1 ? "1 faktura nemá technologii" : `${s.neprirazenych_vydanych} faktury nemají technologii`} — do marže technologií se nepočítá. Přiřaď ji nebo rozděl.</span>
        </div>
      )}
      {d.vydane.length === 0 ? (
        <div className="px-4 md:px-5 py-4 border-t border-line-soft t-telo text-muted">Zatím žádná vydaná faktura.</div>
      ) : d.vydane.map((f) => (
        <div key={f.id} className="px-4 md:px-5 py-3 border-t border-line-soft flex items-center gap-3" data-testid={`eko-vydana-${f.id}`}>
          <DruhStitek f={f} />
          <button onClick={() => nav(`/doklady/${f.id}`)} className="min-w-0 flex-1 text-left">
            <span className="block t-telo font-600 text-ink truncate">{f.cislo || "bez čísla"}</span>
            <span className="block t-meta text-muted truncate">
              {[f.technologie.map((t) => (f.technologie.length > 1 ? `${t.nazev} ${Math.round(t.podil * 100)} %` : t.nazev)).join(", "),
                kratkeDatum(f.datum_vystaveni)].filter(Boolean).join(" · ") || "—"}
            </span>
            {!f.neprirazeno && <StavUhrady f={f} className="md:hidden mt-0.5" />}
          </button>
          {f.duplicita_id && <span className="label-up px-2 py-0.5 rounded-pill bg-[#fdeceb] text-danger shrink-0" title="Stejné číslo od stejné firmy už je nahrané">Duplicita?</span>}
          {f.ocr_stav === "ZPRACOVAVA_SE" ? (
            <span className="t-meta text-accent shrink-0">čte se…</span>
          ) : f.neprirazeno ? (
            <button onClick={() => setPrirazeni(f)} data-testid={`eko-priradit-${f.id}`}
              className="tap md:min-h-0 h-8 px-3 rounded-pill bg-accent text-white t-meta font-700 shrink-0">Přiřadit</button>
          ) : (
            <StavUhrady f={f} className="hidden md:inline-flex w-[150px]" />
          )}
          <span className="amount t-telo font-600 text-ink text-right shrink-0 md:w-[96px]">{fmtKc(f.castka_bez_dph)}</span>
        </div>
      ))}

      {/* Podklad pro účetní program — ProfitPilot fakturu nevystavuje. */}
      <KVyfakturovani polozky={d.k_fakturaci} sPlanem={sPlanem} />

      {/* Náklady technologií */}
      <div className="px-4 md:px-5 pt-2 pb-2.5">
        <h3 className="t-sekce font-700 text-ink">Náklady technologií</h3>
        <p className="t-meta text-muted mt-0.5">
          Přijaté faktury nahráváš v technologii — tady je jejich součet.
          {s.nezaplacenych_dodavatelum > 0 && (
            <> Nezaplaceno dodavatelům <b className="amount text-ink">{fmtKc(s.nezaplaceno_dodavatelum)} Kč</b> s DPH
              {` (${s.nezaplacenych_dodavatelum === 1 ? "1 faktura" : `${s.nezaplacenych_dodavatelum} faktur`}).`}</>
          )}
        </p>
      </div>
      <div className="hidden md:flex items-center gap-3 px-5 py-2 bg-[#fbfaf8] border-t border-line-soft label-up text-muted">
        <span className="w-[30px]" /><span className="flex-1">Technologie</span><span className="w-[150px]">Faktury</span>
        <span className="w-[96px] text-right">Náklady</span><span className="w-[96px] text-right">Výsledek</span><span className="w-[14px]" />
      </div>
      {d.technologie.map((t) => {
        const Ikona = ikonaTechnologie(t.icon);
        const st = stavFaktur(t);
        return (
          <button key={t.id} onClick={() => nav(`/technologie/${t.id}?tab=ekonomika`)} data-testid={`eko-tech-${t.id}`}
            className="w-full text-left px-4 md:px-5 py-3 border-t border-line-soft flex items-center gap-3 hover:bg-inputbg">
            <span className="h-[30px] w-[30px] rounded-[9px] bg-surface text-sec-2 inline-flex items-center justify-center shrink-0" aria-hidden="true">
              <Ikona size={15} strokeWidth={1.8} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block t-telo font-600 text-ink truncate">{t.nazev}</span>
              <span className={`md:hidden block t-meta ${st.cls}`}>{st.text}</span>
            </span>
            <span className={`hidden md:block w-[150px] t-meta ${st.cls}`}>{st.text}</span>
            <span className="amount t-telo text-ink text-right shrink-0 md:w-[96px]">{fmtKc(t.naklady)}</span>
            <span className={`hidden md:block amount t-telo font-700 text-right w-[96px] ${t.vysledek < 0 ? "text-danger" : t.vysledek > 0 ? "text-success" : "text-muted"}`}>
              {t.vysledek < 0 ? "−" : ""}{fmtKc(Math.abs(t.vysledek))}
            </span>
            <ChevronRight size={14} className="text-muted-2 shrink-0" aria-hidden="true" />
          </button>
        );
      })}
      {d.naklady_bez_technologie !== 0 && (
        <button onClick={() => nav("/doklady")} className="w-full text-left px-4 md:px-5 py-3 border-t border-line-soft flex items-center gap-3 hover:bg-inputbg">
          <span className="h-[30px] w-[30px] rounded-[9px] bg-accent/10 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block t-telo font-600 text-ink">Bez technologie</span>
            <span className="block t-meta text-muted">nepočítá se do marže žádné technologie</span>
          </span>
          <span className="amount t-telo text-ink text-right shrink-0 md:w-[96px]">{fmtKc(d.naklady_bez_technologie)}</span>
          <span className="hidden md:block w-[96px]" />
          <ChevronRight size={14} className="text-muted-2 shrink-0" aria-hidden="true" />
        </button>
      )}
      <div className="h-2" />

      {nahrat && (
        <NahratFakturu pripadId={pripadId} technologie={technologie} meta={nazev} pocatecni={nahrat}
          onClose={() => setNahrat(null)} onHotovo={load} />
      )}
      {prirazeni && (
        <PrirazeniDialog doklad={prirazeni} pripadId={pripadId} onClose={() => setPrirazeni(null)} onHotovo={load} />
      )}
    </div>
  );
}

function stavFaktur(t) {
  if (!t.faktur) return { text: "bez faktur", cls: "text-muted" };
  if (t.duplicit) return { text: `${t.faktur} · možná duplicita`, cls: "text-danger" };
  if (t.po_splatnosti) return { text: `${t.faktur} · ${t.po_splatnosti} po splatnosti`, cls: "text-danger" };
  if (t.nezaplacenych) return { text: `${t.faktur} · ${t.nezaplacenych === 1 ? "nezaplacená" : `${t.nezaplacenych} nezaplacené`}`, cls: "text-sec-2" };
  return { text: `${t.faktur} · zaplaceno`, cls: "text-success" };
}

function Dlazdice({ label, hodnota, barva = "text-ink", pod }) {
  return (
    <div className="bg-white px-4 md:px-5 py-3.5 min-w-0">
      <div className="label-up text-muted">{label}</div>
      <div className={`amount text-[17px] md:text-[18px] font-800 mt-1 whitespace-nowrap truncate ${barva}`}>
        {hodnota < 0 ? "−" : ""}{fmtKc(Math.abs(hodnota))} Kč
      </div>
      {pod && <div className="t-meta text-muted">{pod}</div>}
    </div>
  );
}

/** Základ · daň po sazbách · celkem — každý kus nezlomitelně, ať se číslo na telefonu nerozdělí. */
function radekDph(p) {
  const dane = (p.rekapitulace || []).filter((r) => r.dph);
  const casti = [`${fmtKc(p.bez_dph)} bez DPH`,
    ...(dane.length ? dane.map((r) => `DPH ${String(r.sazba).replace(".", ",")} % ${fmtKc(r.dph)}`) : [`DPH ${fmtKc(p.dph)}`]),
    `${fmtKc(p.s_dph)} s DPH`];
  return casti.map((c, i) => <span key={i} className="whitespace-nowrap">{i ? " · " : ""}{c}</span>);
}

function textKopie(p) {
  const druh = p.druh === "ZALOHA" ? `záloha ${String(p.procento).replace(".", ",")} %` : "doplatek";
  const kc = (n) => `${Number(n).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč`;
  const dane = (p.rekapitulace || []).filter((r) => r.dph || r.zaklad);
  return [
    `${p.nazev} — ${druh}`,
    ...(dane.length ? dane.map((r) => `Základ ${String(r.sazba).replace(".", ",")} %: ${kc(r.zaklad)} · DPH ${kc(r.dph)}`) : [`Základ: ${kc(p.bez_dph)}`]),
    `Celkem k úhradě: ${kc(p.s_dph)}`,
  ].join("\n");
}

function KVyfakturovani({ polozky, sPlanem }) {
  const [kopie, setKopie] = useState(null);
  const kopirovat = async (p) => {
    try { await navigator.clipboard.writeText(textKopie(p)); setKopie(p.technologie_id); setTimeout(() => setKopie(null), 1600); }
    catch { window.prompt("Zkopíruj:", textKopie(p)); }
  };
  return (
    <div className="mx-4 md:mx-5 my-3.5 bg-surface rounded-[12px] px-4 py-3.5" data-testid="eko-k-vyfakturovani">
      <div className="flex items-baseline gap-2">
        <span className="label-up text-muted flex-1">K vyfakturování</span>
        <span className="hidden md:inline t-meta text-muted">cena z odsouhlasené nabídky minus vystavené</span>
      </div>
      {polozky.length === 0 ? (
        <div className="t-telo text-sec-2 pt-2">
          {sPlanem ? "Vše z odsouhlasených nabídek je vyfakturované." : "Až klient schválí nabídku, ukážeme tu, kolik vyfakturovat."}
        </div>
      ) : polozky.map((p, i) => (
        <div key={p.technologie_id} className={`flex items-center gap-3 pt-2.5 ${i ? "mt-2.5 border-t border-track" : ""}`}
          data-testid={`eko-navrh-${p.technologie_id}`}>
          <div className="min-w-0 flex-1 t-telo">
            <b className="text-ink">{p.nazev}</b> — {p.druh === "ZALOHA" ? `záloha ${String(p.procento).replace(".", ",")} %` : "doplatek"}
            <div className="amount t-meta text-sec-3">{radekDph(p)}</div>
          </div>
          <button onClick={() => kopirovat(p)} className="tap md:min-h-0 h-[34px] px-3.5 rounded-pill border border-line-strong bg-white t-meta font-600 text-ink shrink-0">
            {kopie === p.technologie_id ? "Zkopírováno" : "Kopírovat"}
          </button>
        </div>
      ))}
    </div>
  );
}
